import { TGenericApiResponse } from "@/components/ui/Filter/filterDropdownRegistry";
import { useCallback, useEffect, useRef, useState } from "react";

export type TDropdownPageResponse<T> = {
  results?: T[];
  current_page?: number;
  total_pages?: number;
  count?: number;
};

type TUseDropdownPaginationArgs<T = unknown> = {
  parentData?: T[];
  parentIsLoading?: boolean;
  parentHasNext?: boolean;
  pageSize: number;
  onOptionsChange?: (options: T[]) => void;
  accumulatedOptions?: T[];
  resetKey?: string;
};

export function useDropdownPagination<T = unknown>({
  parentData,
  parentIsLoading,
  parentHasNext,
  pageSize,
  onOptionsChange,
  accumulatedOptions: persistedOptions,
  resetKey,
}: TUseDropdownPaginationArgs<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [options, setOptions] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const loadedPagesRef = useRef<Set<number>>(new Set([1]));
  const initializedRef = useRef(false);

  const prevParentDataRef = useRef(parentData);
  const parentDataRef = useRef(parentData);
  const prevResetKeyRef = useRef(resetKey);
  const prevOptionsRef = useRef<T[]>([]);
  const onOptionsChangeRef = useRef(onOptionsChange);

  const [accumulatedOptions, setAccumulatedOptions] = useState<T[]>(() => {
    const parentDataLength = parentData?.length ?? 0;
    if (
      persistedOptions &&
      Array.isArray(persistedOptions) &&
      persistedOptions.length > parentDataLength
    ) {
      return persistedOptions;
    }
    return parentData ?? [];
  });

  useEffect(() => {
    parentDataRef.current = parentData;
  }, [parentData]);
  useEffect(() => {
    onOptionsChangeRef.current = onOptionsChange;
  }, [onOptionsChange]);

  // Reset aggressively when parent data reference changes (fresh global dataset)
  useEffect(() => {
    if (prevParentDataRef.current !== parentData) {
      prevParentDataRef.current = parentData;

      const freshData = parentData ?? [];
      setAccumulatedOptions(freshData);

      if (!resetKey) {
        setOptions(freshData);
        setCurrentPage(1);
        loadedPagesRef.current = new Set([1]);
        setHasMore(Boolean(parentHasNext ?? freshData.length >= pageSize));
        initializedRef.current = true;
      }
    }
  }, [parentData, parentHasNext, pageSize, resetKey]);

  // Reset everything when search term (resetKey) changes.
  useEffect(() => {
    if (prevResetKeyRef.current === resetKey) return;
    prevResetKeyRef.current = resetKey;

    setOptions([]);
    setCurrentPage(1);

    // Clear search reverts to the fresh parent data tracking
    if (!resetKey) {
      setAccumulatedOptions(prevParentDataRef.current ?? []);
    }

    setHasMore(false);
    loadedPagesRef.current = new Set([1]);
    initializedRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (parentIsLoading || resetKey || initializedRef.current) return;

    const parentDataLength = parentDataRef.current?.length ?? 0;
    const useAccumulated = accumulatedOptions.length > parentDataLength;
    const initial = useAccumulated
      ? accumulatedOptions
      : (parentDataRef.current ?? []);

    if (initial.length === 0 && parentHasNext == null) return;

    const computedHasNext =
      parentHasNext ??
      (parentDataRef.current
        ? parentDataRef.current.length >= pageSize
        : initial.length >= pageSize);

    loadedPagesRef.current = new Set([1]);
    setCurrentPage(1);
    setOptions(initial);
    setHasMore(Boolean(computedHasNext));
    initializedRef.current = true;
  }, [parentIsLoading, parentHasNext, pageSize, resetKey, accumulatedOptions]);

  // Reliably notify parent on ANY valid mutation to prevent stale mismatch
  useEffect(() => {
    if (prevOptionsRef.current !== accumulatedOptions) {
      prevOptionsRef.current = accumulatedOptions;
      if (onOptionsChangeRef.current) {
        const id = setTimeout(
          () => onOptionsChangeRef.current?.(accumulatedOptions),
          0,
        );
        return () => clearTimeout(id);
      }
    }
  }, [accumulatedOptions]);

  /** Append new page results to existing options. */
  const appendPageResults = useCallback(
    (res?: TGenericApiResponse<T>) => {
      if (!res) return;

      const dataField = res?.data as TDropdownPageResponse<T> | undefined;
      const results = dataField?.results ?? [];
      const loadedPage = dataField?.current_page ?? currentPage;

      const isSearchActive = Boolean(resetKey);
      const shouldReplace = isSearchActive && loadedPage === 1;

      if (!shouldReplace && loadedPagesRef.current.has(loadedPage)) return;

      const resultCount = results?.length ?? 0;

      // Simple check: if we got fewer items than pageSize, we've reached the end
      // Otherwise check totalPages if available
      const totalPages =
        dataField?.total_pages ??
        (dataField?.count ? Math.ceil(dataField.count / pageSize) : undefined);

      const apiHasNext =
        dataField?.count !== undefined && dataField.count <= pageSize
          ? false // Got all items already
          : resultCount < pageSize
            ? false // Got partial page = end reached
            : totalPages !== undefined
              ? loadedPage < totalPages // Use totalPages if available
              : true; // Assume more pages if we got a full page

      setOptions((prev) => {
        const newResults = results?.filter(
          (newItem) =>
            !prev.some((p) => {
              const pId =
                (p as Record<string, unknown>)?.internal_id ??
                (p as Record<string, unknown>)?.id;
              const nId =
                (newItem as Record<string, unknown>)?.internal_id ??
                (newItem as Record<string, unknown>)?.id;
              return pId && nId && pId === nId;
            }),
        );

        const updated = shouldReplace ? results : [...prev, ...newResults];

        // Mark page as loaded here — inside the updater so it's always in sync
        // with the options state that reflects this page's data.
        loadedPagesRef.current.add(loadedPage);

        setAccumulatedOptions((accPrev) =>
          shouldReplace || updated.length > accPrev.length ? updated : accPrev,
        );

        setHasMore(apiHasNext);

        return updated;
      });
    },
    [currentPage, pageSize, resetKey],
  );

  const loadNextPage = useCallback(
    (isLoading: boolean) => {
      if (isLoading) return;
      if (!hasMore) return;

      setCurrentPage((prev) => {
        const nextPage = prev + 1;

        if (loadedPagesRef.current.has(nextPage)) {
          return prev;
        }

        return nextPage;
      });
    },
    [hasMore],
  );

  /**
   * Returns true when the query should fire for `currentPage`.
   * Also uses `options` (live rendered list) — not `accumulatedOptions` — for
   * the length check, for the same reason.
   */
  const shouldFetchCurrentPage = useCallback((): boolean => {
    if (currentPage === 1) return false;

    return !loadedPagesRef.current.has(currentPage);
  }, [currentPage]);

  return {
    currentPage,
    options,
    hasMore,
    loadNextPage,
    appendPageResults,
    pageSize,
    shouldFetchCurrentPage,
  };
}
